import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import { Modal, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaArrowRight, FaHistory, FaTimes, FaLayerGroup, FaFileAlt } from "react-icons/fa";
import programDetails from "../programsdetail";

/**
 * CUSTOM DEBOUNCE HOOK
 */
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const SearchModal = ({ showSearch, handleCloseSearch }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 150); // Reduced delay for snappier feel

  const staticIndex = useMemo(() => [
    { title: "Scholarships", link: "/scholarships", type: "Page", tags: "aid grant funding money finance" },
    { title: "Admission Schedule", link: "/admission-schedule", type: "Page", tags: "deadlines apply dates fall spring" },
    { title: "Fee Structure", link: "/fee-structure", type: "Page", tags: "cost tuition price payment" },
    { title: "Fees Calculator", link: "/fees-calculator", type: "Page", tags: "calculate estimate budget" },
    { title: "Admission Procedure", link: "/admission-procedure", type: "Page", tags: "apply process steps registration" },
    { title: "Campus Tour", link: "/campus-tour", type: "Page", tags: "visit map location environment" },
    { title: "Life at Campus", link: "/life-at-campus", type: "Page", tags: "hostels societies sports cafeteria" },
    { title: "Important Dates", link: "/important-dates", type: "Page", tags: "calendar events holidays" },
  ], []);

  const groupedResults = useMemo(() => {
    const term = debouncedSearchQuery.trim().toLowerCase();
    if (!term) return { programs: [], pages: [] };

    const programs = Object.entries(programDetails)
      .filter(([_, p]) => 
        p.title.toLowerCase().includes(term) || 
        p.department.toLowerCase().includes(term)
      )
      .map(([key, p]) => ({
        id: `prog-${key}`,
        title: p.title,
        subtitle: p.department,
        link: `/program-details/${key}`,
        type: "Program"
      }));

    const pages = staticIndex
      .filter(p => 
        p.title.toLowerCase().includes(term) || 
        p.tags.toLowerCase().includes(term)
      )
      .map(p => ({
        id: `page-${p.link}`,
        title: p.title,
        subtitle: "Information portal",
        link: p.link,
        type: p.type
      }));

    return { programs, pages };
  }, [debouncedSearchQuery, staticIndex]);

  const hasResults = groupedResults.programs.length > 0 || groupedResults.pages.length > 0;
  const totalCount = groupedResults.programs.length + groupedResults.pages.length;

  const closeAndReset = useCallback(() => {
    handleCloseSearch();
    setTimeout(() => setSearchQuery(""), 300);
  }, [handleCloseSearch]);

  return (
    <Modal 
      show={showSearch} 
      onHide={closeAndReset} 
      centered 
      className="cmd-search-modal"
      backdropClassName="cmd-search-backdrop"
    >
      <motion.div 
        initial={{ scale: 0.98, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.98, opacity: 0, y: 10 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="search-container shadow-2xl"
      >
        <header className="search-header">
          <div className="search-icon-wrapper">
            <FaSearch className="search-icon-fixed" />
          </div>
          <Form.Control
            autoFocus
            placeholder="Type to search anything..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input-field"
          />
          <div className="header-right-tools">
            {searchQuery && (
              <button className="search-clear-btn" onClick={() => setSearchQuery("")} aria-label="Clear search">
                <FaTimes />
              </button>
            )}
            <div className="esc-tag d-none d-md-flex">ESC</div>
            <button className="mobile-close-btn d-md-none" onClick={closeAndReset} aria-label="Close search">
              <FaTimes />
            </button>
          </div>
        </header>

        <Modal.Body className="search-results-area custom-scrollbar">
          <AnimatePresence mode="popLayout">
            {hasResults ? (
              <motion.div 
                key="results"
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="results-status-bar">
                  <span>Found {totalCount} results</span>
                  <button className="reset-search-btn" onClick={() => setSearchQuery("")}>Reset</button>
                </div>

                {/* --- PROGRAMS SECTION --- */}
                {groupedResults.programs.length > 0 && (
                  <div className="search-section">
                    <div className="section-label">Degrees & Programs</div>
                    <div className="results-list">
                      {groupedResults.programs.map((item, index) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                        >
                          <Link to={item.link} className="result-item-link" onClick={closeAndReset}>
                            <div className="item-meta">
                              <div className="item-icon-box program">
                                <FaLayerGroup />
                              </div>
                              <div className="item-text">
                                <span className="item-title">{item.title}</span>
                                <span className="item-subtitle">{item.subtitle}</span>
                              </div>
                            </div>
                            <FaArrowRight className="item-arrow" />
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* --- PAGES SECTION --- */}
                {groupedResults.pages.length > 0 && (
                  <div className="search-section">
                    <div className="section-label">Information Portals</div>
                    <div className="results-list">
                      {groupedResults.pages.map((item, index) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: (groupedResults.programs.length + index) * 0.03 }}
                        >
                          <Link to={item.link} className="result-item-link" onClick={closeAndReset}>
                            <div className="item-meta">
                              <div className="item-icon-box page">
                                <FaFileAlt />
                              </div>
                              <div className="item-text">
                                <span className="item-title">{item.title}</span>
                                <span className="item-subtitle">{item.subtitle}</span>
                              </div>
                            </div>
                            <FaArrowRight className="item-arrow" />
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="mobile-search-footer d-md-none">
                   End of results
                </div>
              </motion.div>
            ) : searchQuery ? (
              <motion.div 
                key="empty"
                className="search-empty-state" 
                initial={{ opacity: 0, scale: 0.9 }} 
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <div className="empty-icon-wrap">
                   <FaSearch />
                </div>
                <h5>No results for "<strong>{searchQuery}</strong>"</h5>
                <p>We couldn't find what you're looking for. Try a different keyword.</p>
              </motion.div>
            ) : (
              <motion.div 
                key="onboarding"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="search-onboarding"
              >
                <div className="onboarding-section">
                  <span className="section-title"><FaHistory className="text-primary" /> Popular Searches</span>
                  <div className="tag-cloud">
                    {["BBA", "CS", "Scholarships", "Merit List", "Fee"].map((tag, idx) => (
                      <motion.button 
                        key={tag} 
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        onClick={() => setSearchQuery(tag)} 
                        className="suggest-tag"
                      >
                        {tag}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Modal.Body>

        <footer className="search-keyboard-footer d-none d-md-flex">
          <div className="kb-hint"><span>↵</span> to select</div>
          <div className="kb-hint"><span>↑↓</span> to navigate</div>
        </footer>
      </motion.div>

      <style>{`
        .cmd-search-modal .modal-dialog { max-width: 650px; margin-top: 8vh; transition: margin 0.3s ease; }
        .cmd-search-modal .modal-content { background: transparent; border: none; }
        .cmd-search-backdrop { 
          background: rgba(0, 15, 35, 0.8) !important; 
          backdrop-filter: blur(15px) saturate(180%); 
        }

        .search-container {
          background: #ffffff;
          border-radius: 22px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        .search-header {
          display: flex;
          align-items: center;
          padding: 20px 24px;
          background: #fff;
          border-bottom: 1.5px solid #f1f5f9;
        }

        .search-icon-wrapper { color: #00ced1; font-size: 1.25rem; display: flex; align-items: center; }
        .search-input-field {
          border: none !important;
          box-shadow: none !important;
          font-size: 1.15rem;
          margin-left: 14px;
          color: #1e293b;
          font-weight: 500;
          background: transparent;
        }
        .search-input-field::placeholder { color: #94a3b8; }

        .header-right-tools { display: flex; align-items: center; gap: 12px; }
        .esc-tag { 
          font-size: 0.7rem; font-weight: 800; color: #64748b; 
          border: 1px solid #e2e8f0; padding: 3px 8px; border-radius: 6px; 
          background: #f8fafc;
        }

        .search-clear-btn, .mobile-close-btn {
          background: #f1f5f9; border: none; width: 30px; height: 30px;
          border-radius: 50%; color: #64748b; font-size: 0.8rem;
          display: flex; align-items: center; justify-content: center; transition: 0.2s;
        }
        .search-clear-btn:hover, .mobile-close-btn:hover { background: #e2e8f0; color: #0f172a; }

        .search-results-area { max-height: 500px; min-height: 300px; overflow-y: auto; padding: 0 !important; }
        
        .results-status-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 20px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 600;
        }

        .reset-search-btn {
          background: transparent;
          border: none;
          color: #00ced1;
          font-weight: 700;
          padding: 0;
          cursor: pointer;
        }

        .search-section {
          padding-bottom: 20px;
        }

        .section-label {
          padding: 12px 20px;
          font-size: 0.75rem;
          font-weight: 800;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 1px;
          background: #fff;
          position: sticky;
          top: 0;
          z-index: 5;
          border-bottom: 1px solid #f1f5f9;
        }

        .results-list { padding: 8px 16px; }

        .result-item-link {
          display: flex; justify-content: space-between; align-items: center;
          padding: 14px 16px; border-radius: 12px; text-decoration: none !important;
          margin-bottom: 4px; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid transparent;
        }
        .result-item-link:hover {
          background: #f0fdfa; border-color: #00ced133; transform: scale(1.005);
        }

        .item-meta { display: flex; align-items: center; gap: 14px; }
        .item-icon-box { 
          width: 38px; height: 38px; background: #f8fafc; 
          border-radius: 10px; display: flex; align-items: center; 
          justify-content: center; color: #64748b; font-size: 1rem;
          transition: 0.3s;
        }
        .item-icon-box.program { color: #00ced1; background: #f0fdfa; }
        .item-icon-box.page { color: #f59e0b; background: #fffbeb; }
        .result-item-link:hover .item-icon-box { background: #00ced1; color: white; }

        .item-title { display: block; font-weight: 700; color: #0f172a; font-size: 0.95rem; }
        .item-subtitle { display: block; font-size: 0.75rem; color: #94a3b8; margin-top: 1px; }

        .item-actions { display: flex; align-items: center; gap: 14px; }
        .item-arrow { color: #00ced1; opacity: 0; transition: 0.3s; font-size: 0.9rem; }
        .result-item-link:hover .item-arrow { opacity: 1; transform: translateX(5px); }

        .badge-pill-program { background: #00ced112 !important; color: #00ced1 !important; border: 1px solid #00ced122; font-weight: 700; font-size: 0.7rem; padding: 4px 10px; }
        .badge-pill-page { background: #f8fafc !important; color: #64748b !important; border: 1px solid #e2e8f0; font-weight: 700; font-size: 0.7rem; padding: 4px 10px; }

        .search-onboarding { padding: 15px; }
        .section-title { font-size: 0.85rem; font-weight: 800; color: #1e293b; display: flex; align-items: center; gap: 10px; margin-bottom: 20px; }
        .tag-cloud { display: flex; flex-wrap: wrap; gap: 12px; }
        .suggest-tag { 
          background: #f8fafc; border: 1.5px solid #f1f5f9; padding: 8px 18px; border-radius: 12px; 
          font-size: 0.85rem; color: #475569; font-weight: 600; transition: 0.25s all;
        }
        .suggest-tag:hover { 
          background: #ffffff; color: #00ced1; border-color: #00ced1; 
          box-shadow: 0 10px 15px -3px rgba(0, 206, 209, 0.15); 
          transform: translateY(-2px);
        }

        .search-empty-state { text-align: center; padding: 60px 20px; color: #64748b; }
        .empty-icon-wrap { font-size: 3rem; color: #f1f5f9; margin-bottom: 15px; }
        .search-empty-state h5 { color: #1e293b; font-size: 1.25rem; font-weight: 700; }

        .search-keyboard-footer {
          padding: 14px 24px; background: #f8fafc; border-top: 1.5px solid #f1f5f9;
          display: flex; gap: 28px; justify-content: flex-end;
        }
        .kb-hint { font-size: 0.7rem; color: #94a3b8; display: flex; align-items: center; font-weight: 600; }
        .kb-hint span { 
          background: white; border: 1px solid #e2e8f0; padding: 2px 7px; 
          border-radius: 6px; margin-right: 8px; color: #475569;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }

        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }

        .mobile-search-footer {
          padding: 30px 20px 60px;
          text-align: center;
          font-size: 0.7rem;
          color: #94a3b8;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 2px;
        }

        @media (max-width: 576px) {
          .cmd-search-modal .modal-dialog { max-width: 100%; margin: 0; position: fixed; bottom: 0; left: 0; right: 0; top: 0; }
          .search-container { height: 100%; border-radius: 0; border: none; }
          .search-header { 
            padding: 12px 15px; 
            position: sticky; 
            top: 0; 
            z-index: 100;
            background: #fff;
            border-bottom: 2px solid #00ced1;
          }
          .search-results-area { max-height: none; flex: 1; padding: 0 !important; }
          .result-item-link { padding: 12px 14px; margin-bottom: 2px; border-radius: 0; border: none; border-bottom: 1px solid #f1f5f9; }
          .item-icon-box { width: 34px; height: 34px; font-size: 0.9rem; }
          .item-title { font-size: 0.85rem; }
          .item-subtitle { font-size: 0.7rem; }
          .badge { display: none; }
          .item-actions { gap: 5px; }
          .section-label { 
            background: #f8fafc; 
            color: #00ced1; 
            font-size: 0.7rem; 
            padding: 8px 15px;
            top: 56px; /* offset for sticky header */
          }
        }
      `}</style>
    </Modal>
  );
};

export default memo(SearchModal);